import React, { useMemo, useRef, useState, useCallback, memo, useEffect } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  TooltipProps,
  LegendProps,
  Brush,
  ReferenceLine,
  ReferenceArea
} from 'recharts';
import { exportChartDataToCSV, exportChartDataToJSON, exportChartAsImage } from '../../../utils/exportUtils';
import { DEFAULT_THEME, generateColorPalette } from '../../../utils/colorThemes';

// Dark mode detection hook
function useDarkMode(): boolean {
  const [isDark, setIsDark] = useState(() => {
    if (typeof document !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          setIsDark(document.documentElement.classList.contains('dark'));
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

// Theme colors for charts
const THEME = {
  light: {
    background: 'transparent',
    gridStroke: '#e0e0e0',
    tickColor: '#666666',
    textColor: '#333333',
    tooltipBg: 'rgba(255, 255, 255, 0.95)',
    tooltipBorder: '#e0e0e0',
    tooltipText: '#333333',
    brushBg: 'rgba(0, 123, 255, 0.1)',
    brushStroke: '#007bff',
    cursor: 'rgba(0, 0, 0, 0.1)',
  },
  dark: {
    background: 'transparent',
    gridStroke: 'rgb(51 65 85)', // slate-700
    tickColor: '#94a3b8', // slate-400
    textColor: '#f8fafc', // slate-50
    tooltipBg: 'rgb(30 41 59)', // slate-800
    tooltipBorder: 'rgb(51 65 85)', // slate-700
    tooltipText: '#f8fafc', // slate-50
    brushBg: 'rgba(59, 130, 246, 0.2)',
    brushStroke: '#3b82f6',
    cursor: 'rgba(255, 255, 255, 0.1)',
  },
};

// SVG Icons for export buttons
const ExportIcons = {
  csv: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  json: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M8 13h2" />
      <path d="M8 17h2" />
    </svg>
  ),
  png: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  ),
  svg: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <path d="M9 9l6 6" />
      <path d="M15 9l-6 6" />
    </svg>
  ),
  reset: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  ),
};

export interface ChartDataPoint {
  [key: string]: any;
}

export interface ChartSeries {
  key: string;
  name: string;
  color?: string;
  type?: 'bar' | 'line' | 'area';
}

export interface ChartProps {
  data: ChartDataPoint[];
  type: 'bar' | 'line' | 'pie' | 'area' | 'combo';
  colors?: string[];
  responsive?: boolean;
  showTooltip?: boolean;
  showLegend?: boolean;
  width?: number;
  height?: number;
  xAxisKey?: string;
  yAxisKey?: string | string[];
  series?: ChartSeries[];
  className?: string;
  title?: string;
  subtitle?: string;
  showGrid?: boolean;
  animate?: boolean;
  customTooltip?: React.ComponentType<TooltipProps<any, any>>;
  customLegend?: React.ComponentType<LegendProps>;
  onDataPointClick?: (data: any, event: React.SyntheticEvent) => void;
  formatXAxis?: (value: any) => string;
  formatYAxis?: (value: any) => string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  // Advanced features
  enableZoom?: boolean;
  enablePan?: boolean;
  enableBrush?: boolean;
  enableExport?: boolean;
  exportFilename?: string;
  showExportButtons?: boolean;
  zoomDomain?: { x?: [number, number]; y?: [number, number] };
  onZoomChange?: (domain: { x?: [number, number]; y?: [number, number] }) => void;
  // Enhanced tooltips
  tooltipFormatter?: (value: any, name: string, props: any) => [string, string];
  tooltipLabelFormatter?: (label: any, payload: readonly any[]) => React.ReactNode;
  tooltipContentStyle?: React.CSSProperties;
  // Performance optimizations
  maxDataPoints?: number;
  enableDataSampling?: boolean;
  samplingThreshold?: number;
  enableVirtualization?: boolean;
  debounceResize?: number;
  // Responsive breakpoints
  responsiveBreakpoints?: {
    mobile?: number;
    tablet?: number;
    desktop?: number;
  };
  // Reference lines and areas
  referenceLines?: Array<{
    x?: number | string;
    y?: number;
    stroke?: string;
    strokeDasharray?: string;
    label?: string;
  }>;
  referenceAreas?: Array<{
    x1?: number | string;
    x2?: number | string;
    y1?: number;
    y2?: number;
    fill?: string;
    fillOpacity?: number;
    label?: string;
  }>;
}

const DEFAULT_MARGIN = { top: 20, right: 30, left: 20, bottom: 5 };

export const Chart = memo(function Chart({
  data,
  type,
  colors,
  responsive = true,
  showTooltip = true,
  showLegend = true,
  width = 400,
  height = 300,
  xAxisKey = 'name',
  yAxisKey = 'value',
  series,
  className = '',
  title,
  subtitle,
  showGrid = true,
  animate = true,
  customTooltip,
  customLegend,
  onDataPointClick,
  formatXAxis,
  formatYAxis,
  margin = DEFAULT_MARGIN,
  // Advanced features
  enableZoom = false,
  enablePan = false,
  enableBrush = false,
  enableExport = false,
  exportFilename = 'chart-data',
  showExportButtons = false,
  zoomDomain,
  onZoomChange,
  // Enhanced tooltips
  tooltipFormatter,
  tooltipLabelFormatter,
  tooltipContentStyle,
  // Responsive breakpoints
  responsiveBreakpoints = {
    mobile: 768,
    tablet: 1024,
    desktop: 1200
  },
  // Reference lines and areas
  referenceLines = [],
  referenceAreas = [],
  // Performance optimizations
  maxDataPoints = 1000,
  enableDataSampling = true,
  samplingThreshold = 500,
  enableVirtualization = false,
  debounceResize = 250
}: ChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [zoomState, setZoomState] = useState<{ x?: [number, number]; y?: [number, number] }>(zoomDomain || {});
  const [isExporting, setIsExporting] = useState(false);
  const resizeTimeoutRef = useRef<NodeJS.Timeout>();

  // Dark mode detection
  const isDarkMode = useDarkMode();
  const theme = isDarkMode ? THEME.dark : THEME.light;

  // Handle window resize for responsive behavior with debouncing
  React.useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, debounceResize);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [debounceResize]);

  // Data sampling for performance optimization
  const processedData = useMemo(() => {
    if (!enableDataSampling || data.length <= samplingThreshold) {
      return data;
    }

    // Simple sampling algorithm - take every nth point
    const sampleRate = Math.ceil(data.length / maxDataPoints);
    const sampledData = data.filter((_, index) => index % sampleRate === 0);
    
    // Always include the last data point
    if (data.length > 0 && sampledData[sampledData.length - 1] !== data[data.length - 1]) {
      sampledData.push(data[data.length - 1]);
    }
    
    return sampledData;
  }, [data, enableDataSampling, samplingThreshold, maxDataPoints]);

  // Generate colors using theme
  const chartColors = useMemo(() => {
    if (colors) return colors;
    const seriesCount = series?.length || (Array.isArray(yAxisKey) ? yAxisKey.length : 1);
    return generateColorPalette(Math.max(seriesCount, processedData.length), DEFAULT_THEME);
  }, [colors, series, yAxisKey, processedData.length]);

  // Responsive chart dimensions
  const responsiveHeight = useMemo(() => {
    if (!responsive) return height;
    
    if (windowWidth <= responsiveBreakpoints.mobile!) {
      return Math.max(250, height * 0.7);
    } else if (windowWidth <= responsiveBreakpoints.tablet!) {
      return Math.max(300, height * 0.85);
    }
    return height;
  }, [responsive, height, windowWidth, responsiveBreakpoints]);

  const chartMargin = useMemo(() => ({
    top: margin?.top ?? DEFAULT_MARGIN.top,
    right: margin?.right ?? DEFAULT_MARGIN.right,
    left: margin?.left ?? DEFAULT_MARGIN.left,
    bottom: margin?.bottom ?? DEFAULT_MARGIN.bottom
  }), [margin]);

  const chartProps = {
    data: processedData,
    width: responsive ? undefined : width,
    height: responsive ? undefined : responsiveHeight,
    margin: chartMargin
  };

  const yAxisKeys = useMemo(() => {
    if (series) {
      return series.map(s => s.key);
    }
    return Array.isArray(yAxisKey) ? yAxisKey : [yAxisKey];
  }, [yAxisKey, series]);

  const getSeriesColor = (index: number, seriesItem?: ChartSeries) => {
    return seriesItem?.color || chartColors[index % chartColors.length];
  };

  // Export functions (use original data, not sampled)
  const handleExportCSV = useCallback(() => {
    exportChartDataToCSV(data, `${exportFilename}.csv`);
  }, [data, exportFilename]);

  const handleExportJSON = useCallback(() => {
    exportChartDataToJSON(data, `${exportFilename}.json`);
  }, [data, exportFilename]);

  const handleExportImage = useCallback(async (format: 'png' | 'svg' = 'png') => {
    if (!chartRef.current) return;
    
    setIsExporting(true);
    try {
      await exportChartAsImage(chartRef.current, `${exportFilename}.${format}`, format);
    } catch (error) {
      console.error('Failed to export chart as image:', error);
    } finally {
      setIsExporting(false);
    }
  }, [exportFilename]);

  // Zoom and pan handlers - temporarily unused
  // const handleZoomChange = useCallback((domain: any) => {
  //   const newZoomState = { x: domain?.x, y: domain?.y };
  //   setZoomState(newZoomState);
  //   onZoomChange?.(newZoomState);
  // }, [onZoomChange]);

  const renderTooltip = () => {
    if (!showTooltip) return null;
    if (customTooltip) {
      const CustomTooltip = customTooltip;
      return <CustomTooltip />;
    }

    return (
      <Tooltip
        formatter={(tooltipFormatter || formatYAxis) as any}
        labelFormatter={tooltipLabelFormatter || formatXAxis}
        contentStyle={{
          backgroundColor: theme.tooltipBg,
          border: `1px solid ${theme.tooltipBorder}`,
          borderRadius: '8px',
          boxShadow: isDarkMode
            ? '0 4px 12px rgba(0, 0, 0, 0.4)'
            : '0 4px 12px rgba(0, 0, 0, 0.15)',
          fontSize: '14px',
          color: theme.tooltipText,
          ...tooltipContentStyle
        }}
        labelStyle={{ color: theme.tooltipText }}
        itemStyle={{ color: theme.tickColor }}
        cursor={{ fill: theme.cursor }}
        animationDuration={200}
      />
    );
  };

  const renderLegend = () => {
    if (!showLegend) return null;
    if (customLegend) {
      const CustomLegend = customLegend;
      return <CustomLegend />;
    }
    return <Legend wrapperStyle={{ paddingTop: '16px' }} />;
  };

  const renderReferenceLines = () => {
    return referenceLines.map((line, index) => (
      <ReferenceLine
        key={`ref-line-${index}`}
        x={line.x}
        y={line.y}
        stroke={line.stroke || '#666'}
        strokeDasharray={line.strokeDasharray || '5 5'}
        label={line.label}
      />
    ));
  };

  const renderReferenceAreas = () => {
    return referenceAreas.map((area, index) => (
      <ReferenceArea
        key={`ref-area-${index}`}
        x1={area.x1}
        x2={area.x2}
        y1={area.y1}
        y2={area.y2}
        fill={area.fill || 'rgba(0, 0, 0, 0.1)'}
        fillOpacity={area.fillOpacity || 0.1}
        label={area.label}
      />
    ));
  };

  const renderChart = () => {
    switch (type) {
      case 'bar':
        return (
          <BarChart
            {...chartProps}
            onClick={onDataPointClick ? (data, event) => onDataPointClick(data, event) : undefined}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={theme.gridStroke} />}
            <XAxis
              dataKey={xAxisKey}
              tickFormatter={formatXAxis}
              tick={{ fontSize: 12, fill: theme.tickColor }}
              stroke={theme.gridStroke}
              domain={zoomState.x || ['dataMin', 'dataMax']}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12, fill: theme.tickColor }}
              stroke={theme.gridStroke}
              domain={zoomState.y || ['dataMin', 'dataMax']}
            />
            {renderTooltip()}
            {renderLegend()}
            {renderReferenceAreas()}
            {renderReferenceLines()}
            {yAxisKeys.map((key, index) => {
              const seriesItem = series?.[index];
              return (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={getSeriesColor(index, seriesItem)}
                  name={seriesItem?.name || key}
                  animationDuration={animate ? 1000 : 0}
                  radius={[2, 2, 0, 0]}
                />
              );
            })}
            {enableBrush && (
              <Brush
                dataKey={xAxisKey}
                height={30}
                stroke={theme.brushStroke}
                fill={theme.brushBg}
                tickFormatter={formatXAxis}
              />
            )}
          </BarChart>
        );

      case 'line':
        return (
          <LineChart
            {...chartProps}
            onClick={onDataPointClick ? (data, event) => onDataPointClick(data, event) : undefined}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={theme.gridStroke} />}
            <XAxis
              dataKey={xAxisKey}
              tickFormatter={formatXAxis}
              tick={{ fontSize: 12, fill: theme.tickColor }}
              stroke={theme.gridStroke}
              domain={zoomState.x || ['dataMin', 'dataMax']}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12, fill: theme.tickColor }}
              stroke={theme.gridStroke}
              domain={zoomState.y || ['dataMin', 'dataMax']}
            />
            {renderTooltip()}
            {renderLegend()}
            {renderReferenceAreas()}
            {renderReferenceLines()}
            {yAxisKeys.map((key, index) => {
              const seriesItem = series?.[index];
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={getSeriesColor(index, seriesItem)}
                  name={seriesItem?.name || key}
                  strokeWidth={2}
                  dot={{ r: 3, strokeWidth: 2 }}
                  activeDot={{ r: 6, strokeWidth: 2 }}
                  animationDuration={animate ? 1000 : 0}
                />
              );
            })}
            {enableBrush && (
              <Brush
                dataKey={xAxisKey}
                height={30}
                stroke={theme.brushStroke}
                fill={theme.brushBg}
                tickFormatter={formatXAxis}
              />
            )}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart
            {...chartProps}
            onClick={onDataPointClick ? (data, event) => onDataPointClick(data, event) : undefined}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={theme.gridStroke} />}
            <XAxis
              dataKey={xAxisKey}
              tickFormatter={formatXAxis}
              tick={{ fontSize: 12, fill: theme.tickColor }}
              stroke={theme.gridStroke}
              domain={zoomState.x || ['dataMin', 'dataMax']}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12, fill: theme.tickColor }}
              stroke={theme.gridStroke}
              domain={zoomState.y || ['dataMin', 'dataMax']}
            />
            {renderTooltip()}
            {renderLegend()}
            {renderReferenceAreas()}
            {renderReferenceLines()}
            {yAxisKeys.map((key, index) => {
              const seriesItem = series?.[index];
              const color = getSeriesColor(index, seriesItem);
              return (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  fill={color}
                  fillOpacity={0.3}
                  strokeWidth={2}
                  name={seriesItem?.name || key}
                  animationDuration={animate ? 1000 : 0}
                />
              );
            })}
            {enableBrush && (
              <Brush
                dataKey={xAxisKey}
                height={30}
                stroke={theme.brushStroke}
                fill={theme.brushBg}
                tickFormatter={formatXAxis}
              />
            )}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart 
            {...chartProps}
            onClick={onDataPointClick ? (data, event) => onDataPointClick(data, event) : undefined}
          >
            {renderTooltip()}
            {renderLegend()}
            <Pie
              data={processedData}
              dataKey={Array.isArray(yAxisKey) ? yAxisKey[0] : yAxisKey}
              nameKey={xAxisKey}
              cx="50%"
              cy="50%"
              outerRadius={Math.min(width, responsiveHeight) * 0.35}
              innerRadius={0}
              fill={chartColors[0]}
              animationDuration={animate ? 1000 : 0}
              label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(1)}%`}
              labelLine={false}
            >
              {processedData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={chartColors[index % chartColors.length]}
                  stroke="#fff"
                  strokeWidth={2}
                />
              ))}
            </Pie>
          </PieChart>
        );

      case 'combo':
        return (
          <BarChart
            {...chartProps}
            onClick={onDataPointClick ? (data, event) => onDataPointClick(data, event) : undefined}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={theme.gridStroke} />}
            <XAxis
              dataKey={xAxisKey}
              tickFormatter={formatXAxis}
              tick={{ fontSize: 12, fill: theme.tickColor }}
              stroke={theme.gridStroke}
              domain={zoomState.x || ['dataMin', 'dataMax']}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 12, fill: theme.tickColor }}
              stroke={theme.gridStroke}
              domain={zoomState.y || ['dataMin', 'dataMax']}
            />
            {renderTooltip()}
            {renderLegend()}
            {renderReferenceAreas()}
            {renderReferenceLines()}
            {series?.map((seriesItem, index) => {
              const color = getSeriesColor(index, seriesItem);
              if (seriesItem.type === 'line') {
                return (
                  <Line
                    key={seriesItem.key}
                    type="monotone"
                    dataKey={seriesItem.key}
                    stroke={color}
                    name={seriesItem.name}
                    strokeWidth={2}
                    dot={{ r: 3, strokeWidth: 2 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                    animationDuration={animate ? 1000 : 0}
                  />
                );
              } else {
                return (
                  <Bar
                    key={seriesItem.key}
                    dataKey={seriesItem.key}
                    fill={color}
                    name={seriesItem.name}
                    animationDuration={animate ? 1000 : 0}
                    radius={[2, 2, 0, 0]}
                  />
                );
              }
            })}
            {enableBrush && (
              <Brush
                dataKey={xAxisKey}
                height={30}
                stroke={theme.brushStroke}
                fill={theme.brushBg}
                tickFormatter={formatXAxis}
              />
            )}
          </BarChart>
        );

      default:
        return (
          <div className="flex items-center justify-center p-10 text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded m-4">
            <p className="m-0 font-medium">Unsupported chart type: {type}</p>
          </div>
        );
    }
  };

  const ChartComponent = responsive ? (
    <ResponsiveContainer
      width="100%"
      height={responsiveHeight}
      style={{ background: 'transparent' }}
    >
      {renderChart()}
    </ResponsiveContainer>
  ) : (
    renderChart()
  );

  return (
    <div
      className={`bg-transparent rounded-lg overflow-hidden dark:bg-slate-800 dark:text-slate-200 dark:shadow-md ${className} ${isExporting ? 'relative' : ''}`}
      ref={chartRef}
    >
      {(title || subtitle || showExportButtons) && (
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 px-5 pt-4 pb-4 border-b border-neutral-200 dark:border-slate-700 mb-4">
          <div className="flex-1">
            {title && <h3 className="m-0 mb-2 text-lg font-semibold text-neutral-800 dark:text-slate-50">{title}</h3>}
            {subtitle && <p className="m-0 text-sm text-neutral-500 dark:text-slate-400 leading-relaxed">{subtitle}</p>}
          </div>
          {(enableExport || showExportButtons) && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleExportCSV}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-100 dark:bg-slate-700 border border-neutral-300 dark:border-slate-600 rounded text-xs cursor-pointer transition-all duration-200 whitespace-nowrap text-neutral-700 dark:text-slate-200 hover:bg-neutral-200 dark:hover:bg-slate-600 hover:border-neutral-400 dark:hover:border-slate-500 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Export as CSV"
                disabled={isExporting}
              >
                {ExportIcons.csv}
                <span>CSV</span>
              </button>
              <button
                onClick={handleExportJSON}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-100 dark:bg-slate-700 border border-neutral-300 dark:border-slate-600 rounded text-xs cursor-pointer transition-all duration-200 whitespace-nowrap text-neutral-700 dark:text-slate-200 hover:bg-neutral-200 dark:hover:bg-slate-600 hover:border-neutral-400 dark:hover:border-slate-500 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Export as JSON"
                disabled={isExporting}
              >
                {ExportIcons.json}
                <span>JSON</span>
              </button>
              <button
                onClick={() => handleExportImage('png')}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-100 dark:bg-slate-700 border border-neutral-300 dark:border-slate-600 rounded text-xs cursor-pointer transition-all duration-200 whitespace-nowrap text-neutral-700 dark:text-slate-200 hover:bg-neutral-200 dark:hover:bg-slate-600 hover:border-neutral-400 dark:hover:border-slate-500 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Export as PNG"
                disabled={isExporting}
              >
                {ExportIcons.png}
                <span>PNG</span>
              </button>
              <button
                onClick={() => handleExportImage('svg')}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-neutral-100 dark:bg-slate-700 border border-neutral-300 dark:border-slate-600 rounded text-xs cursor-pointer transition-all duration-200 whitespace-nowrap text-neutral-700 dark:text-slate-200 hover:bg-neutral-200 dark:hover:bg-slate-600 hover:border-neutral-400 dark:hover:border-slate-500 disabled:opacity-60 disabled:cursor-not-allowed"
                title="Export as SVG"
                disabled={isExporting}
              >
                {ExportIcons.svg}
                <span>SVG</span>
              </button>
            </div>
          )}
        </div>
      )}
      <div className="relative px-5 pt-4 pb-5 bg-transparent">
        {ChartComponent}
        {isExporting && (
          <div className="absolute inset-0 bg-white/90 dark:bg-slate-900/90 flex flex-col items-center justify-center gap-3 z-10">
            <div className="w-8 h-8 border-3 border-neutral-200 border-t-primary-500 rounded-full animate-spin"></div>
            <span className="text-neutral-600 dark:text-slate-400">Exporting...</span>
          </div>
        )}
      </div>
      {(enableZoom || enablePan) && (
        <div className="px-5 py-3 border-t border-neutral-200 dark:border-slate-700 flex justify-center gap-3">
          <button
            onClick={() => {
              setZoomState({});
              onZoomChange?.({});
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white border-none rounded text-xs cursor-pointer transition-colors duration-200 hover:bg-primary-700"
            title="Reset Zoom"
          >
            {ExportIcons.reset}
            <span>Reset Zoom</span>
          </button>
        </div>
      )}
    </div>
  );
});